import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  X, 
  Plus, 
  ImageIcon, 
  BookOpen, 
  Tag,
  Loader2,
  PenTool,
  Move,
  Trash2,
  Edit,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../utils/cloudinaryUpload';

const EditComicStreamlined = () => {
  const { id } = useParams();
  const [comicData, setComicData] = useState({
    title: '',
    description: '',
    categories: [],
    tags: []
  });
  const [episodes, setEpisodes] = useState([
    {
      id: 1,
      title: 'Episode 1',
      slides: []
    }
  ]);
  const [currentEpisode, setCurrentEpisode] = useState(0);
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  
  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchComicData();
  }, [id]);

  const fetchComicData = async () => {
    try {
      setLoadingData(true);
      const response = await axios.get(`${API}/streamlined-comics/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const comic = response.data;
      
      // Set basic comic data
      setComicData({
        title: comic.title,
        description: comic.description || '',
        categories: comic.categories || [],
        tags: comic.tags || []
      });

      // Handle episodes from streamlined comic
      if (comic.episodes && Array.isArray(comic.episodes)) {
        const reconstructedEpisodes = comic.episodes.map((episode, index) => ({
          id: index + 1,
          title: episode.title || `Episode ${index + 1}`,
          slides: episode.slides.map((slide, slideIndex) => ({
            id: Date.now() + Math.random() + slideIndex,
            image: slide,
            filename: `slide-${slideIndex + 1}`
          }))
        }));
        
        setEpisodes(reconstructedEpisodes);
      } else {
        // Fallback - create default episode
        setEpisodes([{
          id: 1,
          title: 'Episode 1',
          slides: []
        }]);
      }
      
    } catch (error) {
      console.error('Error fetching comic:', error);
      setError('Failed to load comic data');
      toast.error('Failed to load comic');
    } finally {
      setLoadingData(false);
    }
  };

  const handleComicDataChange = (field, value) => {
    setComicData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addCategory = () => {
    if (newCategory.trim() && !comicData.categories.includes(newCategory.trim())) {
      setComicData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const removeCategory = (category) => {
    setComicData(prev => ({
      ...prev,
      categories: prev.categories.filter(cat => cat !== category)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !comicData.tags.includes(newTag.trim())) {
      setComicData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setComicData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Episode Management
  const addNewEpisode = () => {
    const newEpisodeNumber = episodes.length + 1;
    setEpisodes(prev => [
      ...prev,
      {
        id: newEpisodeNumber,
        title: `Episode ${newEpisodeNumber}`,
        slides: []
      }
    ]);
  };

  const updateEpisodeTitle = (episodeIndex, title) => {
    setEpisodes(prev => prev.map((ep, idx) => 
      idx === episodeIndex ? { ...ep, title } : ep
    ));
  };

  const deleteEpisode = (episodeIndex) => {
    if (episodes.length > 1) {
      setEpisodes(prev => prev.filter((_, idx) => idx !== episodeIndex));
      if (currentEpisode >= episodeIndex && currentEpisode > 0) {
        setCurrentEpisode(prev => prev - 1);
      }
    }
  };

  // Slide Management - Now using Cloudinary
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    let uploaded = 0;
    const totalFiles = files.length;
    
    for (const file of files) {
      try {
        // Upload to Cloudinary
        const result = await uploadImage(file, token, 'comic-panels', (progress) => {
          setUploadProgress(Math.round((uploaded / totalFiles) * 100 + (progress / totalFiles)));
        });
        
        if (result.success) {
          const newSlide = {
            id: Date.now() + Math.random(),
            image: result.url,  // Cloudinary URL
            public_id: result.public_id,
            filename: file.name,
            width: result.width,
            height: result.height
          };
          
          setEpisodes(prev => prev.map((ep, idx) => 
            idx === currentEpisode 
              ? { ...ep, slides: [...ep.slides, newSlide] }
              : ep
          ));
          
          uploaded++;
          setUploadProgress(Math.round((uploaded / totalFiles) * 100));
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    setUploadProgress(0);
    
    if (uploaded > 0) {
      toast.success(`Uploaded ${uploaded} image${uploaded > 1 ? 's' : ''} successfully!`);
    }
  };

  const removeSlide = (slideId) => {
    setEpisodes(prev => prev.map((ep, idx) => 
      idx === currentEpisode 
        ? { ...ep, slides: ep.slides.filter(slide => slide.id !== slideId) }
        : ep
    ));
  };

  const moveSlide = (slideIndex, direction) => {
    const episode = episodes[currentEpisode];
    if (!episode) return;
    
    const newIndex = direction === 'up' ? slideIndex - 1 : slideIndex + 1;
    if (newIndex < 0 || newIndex >= episode.slides.length) return;

    const newSlides = [...episode.slides];
    [newSlides[slideIndex], newSlides[newIndex]] = [newSlides[newIndex], newSlides[slideIndex]];
    
    setEpisodes(prev => prev.map((ep, idx) => 
      idx === currentEpisode ? { ...ep, slides: newSlides } : ep
    ));
  };

  const validateForm = () => {
    if (!comicData.title.trim()) {
      setError('Comic title is required');
      return false;
    }

    if (episodes.length === 0) {
      setError('At least one episode is required');
      return false;
    }

    const hasSlides = episodes.some(ep => ep.slides.length > 0);
    if (!hasSlides) {
      setError('At least one episode must have slides');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Update comic with episodes using streamlined format
      const comicPayload = {
        title: comicData.title,
        description: comicData.description,
        categories: comicData.categories,
        tags: comicData.tags,
        episodes: episodes.map(ep => ({
          title: ep.title,
          slides: ep.slides.map(slide => slide.image)
        }))
      };

      await axios.put(`${API}/streamlined-comics/${id}`, comicPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Comic updated successfully!');
      navigate(`/streamlined-comic/${id}`);
      
    } catch (error) {
      console.error('Error updating comic:', error);
      setError(error.response?.data?.detail || 'Failed to update comic');
      toast.error('Failed to update comic');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">Loading comic data...</p>
        </div>
      </div>
    );
  }

  const currentEpisodeData = episodes[currentEpisode];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
            <Edit className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-fredoka">Edit Comic</h1>
          <p className="text-gray-400 mt-2">Update your comic with episodes and manage slides easily</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Comic Details */}
          <Card className="bg-gray-800/80 backdrop-blur border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-white">
                <BookOpen className="w-5 h-5 text-purple-400" />
                <span>Comic Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-300">Comic Title *</Label>
                  <Input
                    id="title"
                    value={comicData.title}
                    onChange={(e) => handleComicDataChange('title', e.target.value)}
                    placeholder="Enter comic title"
                    required
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-300">Description</Label>
                <Textarea
                  id="description"
                  value={comicData.description}
                  onChange={(e) => handleComicDataChange('description', e.target.value)}
                  placeholder="Describe your comic story..."
                  rows={3}
                  className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                />
              </div>

              {/* Categories */}
              <div className="space-y-2">
                <Label className="text-gray-300">Categories</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {comicData.categories.map(category => (
                    <Badge key={category} variant="secondary" className="bg-purple-600/30 text-purple-300 border-purple-500/50 flex items-center space-x-1">
                      <span>{category}</span>
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-400" 
                        onClick={() => removeCategory(category)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Add category"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                  />
                  <Button type="button" onClick={addCategory} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label className="text-gray-300">Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {comicData.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="border-cyan-500/50 text-cyan-300 flex items-center space-x-1">
                      <Tag className="w-3 h-3" />
                      <span>{tag}</span>
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-400" 
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                  />
                  <Button type="button" onClick={addTag} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Episodes Management */}
          <Card className="bg-gray-800/80 backdrop-blur border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-white">
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  <span>Episodes</span>
                </div>
                <Button type="button" onClick={addNewEpisode} size="sm" className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Episode
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Episode Tabs */}
              <div className="flex space-x-2 overflow-x-auto">
                {episodes.map((episode, index) => (
                  <div key={episode.id} className="flex items-center space-x-2 min-w-max">
                    <Button
                      type="button"
                      variant={currentEpisode === index ? "default" : "outline"}
                      onClick={() => setCurrentEpisode(index)}
                      size="sm"
                      className={currentEpisode === index 
                        ? "bg-purple-600 hover:bg-purple-700 text-white" 
                        : "border-gray-600 text-gray-300 hover:bg-gray-700"}
                    >
                      Episode {index + 1}
                    </Button>
                    {episodes.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteEpisode(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Current Episode Details */}
              {currentEpisodeData && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Episode Title</Label>
                    <Input
                      value={currentEpisodeData.title}
                      onChange={(e) => updateEpisodeTitle(currentEpisode, e.target.value)}
                      placeholder="Enter episode title"
                      className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                    />
                  </div>

                  {/* Slide Management */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-300">Slides ({currentEpisodeData.slides.length})</Label>
                      <div>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id={`episode-${currentEpisode}-upload`}
                          disabled={uploading}
                        />
                        <Button
                          type="button"
                          onClick={() => document.getElementById(`episode-${currentEpisode}-upload`).click()}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading {uploadProgress}%
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Add Slides
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Upload Progress Bar */}
                    {uploading && (
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Slides Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {currentEpisodeData.slides.map((slide, slideIndex) => (
                        <Card key={slide.id} className="relative group bg-gray-700/50 border-gray-600">
                          <CardContent className="p-3">
                            <div className="aspect-square relative overflow-hidden rounded-lg">
                              <img
                                src={slide.image}
                                alt={`Slide ${slideIndex + 1}`}
                                className="w-full h-full object-cover"
                              />
                              
                              {/* Slide Controls */}
                              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => moveSlide(slideIndex, 'up')}
                                  disabled={slideIndex === 0}
                                  className="text-white hover:bg-white hover:bg-opacity-20"
                                >
                                  <Move className="w-4 h-4 rotate-180" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => moveSlide(slideIndex, 'down')}
                                  disabled={slideIndex === currentEpisodeData.slides.length - 1}
                                  className="text-white hover:bg-white hover:bg-opacity-20"
                                >
                                  <Move className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeSlide(slide.id)}
                                  className="text-red-400 hover:bg-red-500 hover:bg-opacity-20"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-2 truncate">
                              Slide {slideIndex + 1}
                            </p>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Upload Placeholder */}
                      <Card 
                        className="cursor-pointer border-dashed border-2 border-gray-600 hover:border-purple-400 transition-colors bg-gray-700/30"
                        onClick={() => document.getElementById(`episode-${currentEpisode}-upload`).click()}
                      >
                        <CardContent className="p-6 flex flex-col items-center justify-center aspect-square">
                          <Upload className="w-8 h-8 text-gray-500 mb-2" />
                          <p className="text-sm text-gray-500 text-center">
                            Click to add slides
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-3 text-white"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating Comic...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Update Comic
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditComicStreamlined;